import React from 'react'
import { Popover, PopoverProps, Radio } from 'src/antd'
import styled from 'styled-components'

import { blue100, textPrimary } from 'src/styles/color-theme'

const PopoverWithOverlayClassName = ({
  className,
  ...props
}: PopoverProps & { className?: string }) => (
  <Popover {...props} overlayClassName={className} />
)

export const StyledDatePickerWrapper = styled.div`
  position: relative;
  .ant-picker-range {
    opacity: 0;
    height: 0;
    overflow: hidden;
    & + div {
      position: static !important;
      margin-top: -27px;
      .ant-picker-dropdown {
        position: static;
      }
      .ant-picker-panel-container {
        box-shadow: none;
        width: 100%;
      }
      .ant-picker-range-arrow {
        display: none;
      }
      .ant-picker-panel {
        border-bottom: none;
        width: 50%;
      }
      .ant-picker-dropdown {
        top: 0 !important;
      }
      .ant-picker-panel-container .ant-picker-panels {
        display: flex;
      }
      .ant-picker-date-panel {
        width: 100% !important;
        .ant-picker-content {
          width: 100%;
        }
      }
      .ant-picker-header {
        button {
          color: ${textPrimary};
        }
      }
    }
  }

  /* The shadcn rendering, beside the antd one above — the seam re-emits none
     of antd's picker class names and keys its parts by data-slot
     (src/antd/DatePicker/DatePicker.shadcn.tsx). What these rules have to
     reproduce is what the antd block achieves, not its selectors: the range
     inputs collapse to nothing, and the always-open panel sits INLINE in this
     660px popover instead of floating over it.

     The panel is a Radix PopoverContent, portalled into this wrapper by the
     picker's own getPopupContainer, but positioned by Radix with
     position: fixed + a transform written INLINE on its popper wrapper — so a
     position: static twin alone is not enough and each of those inline
     declarations needs an !important rule to beat it. */
  [data-slot='picker'][data-range] {
    opacity: 0;
    height: 0;
    min-height: 0;
    padding: 0;
    border: none;
    overflow: hidden;
  }

  /* [data-radix-popper-content-wrapper] is a RADIX INTERNAL: it is the
     positioner Radix wraps a PopoverContent in, not part of any documented
     API. radix-ui is on a caret (^1.6.7), not pinned the way react-day-picker
     and lucide-react are, and this is the first production file in the repo
     to depend on that attribute - jsdom runs no layout, so nothing in the
     suite fails if a Radix release renames or drops it and this panel starts
     floating over the popover again. A radix-ui bump therefore needs THIS
     FILE looked at in a browser.

     Scoped with :has() to the wrapper that actually holds THIS picker's panel:
     the popper wrapper is created by Radix and carries no class of ours, so a
     wrapper class cannot express it, and an untargeted rule would silently
     de-position any other Radix popup that later lands inside this wrapper. */
  [data-radix-popper-content-wrapper]:has([data-slot='picker-dropdown']) {
    position: static !important;
    transform: none !important;
    width: 100% !important;
    min-width: 0 !important;
    margin-top: -27px;
  }

  [data-slot='picker-dropdown'] {
    width: 100%;
    max-height: none;
    box-shadow: none;
    border: none;
    border-radius: 0;
    animation: none;
  }

  [data-slot='picker-panels'] {
    display: flex;
    width: 100%;

    > [data-slot='picker-panel'] {
      width: 50%;
      border-bottom: none;
    }

    [data-slot='picker-content'] {
      width: 100%;
    }
  }

  [data-slot='picker-header'] {
    button {
      color: ${textPrimary};
    }
  }
`

export const StyledPopover = styled(PopoverWithOverlayClassName)`
  animation-duration: 0s;
  padding-top: 0px;
  .ant-popover-inner-content {
    padding: 0;
  }
  .ant-popover-arrow {
    display: none;
  }
`

export const StyledRadioGroup = styled(Radio.Group)`
  /* Both renderings — see src/antd/Radio.shadcn.tsx. */
  .ant-radio-button-wrapper,
  [data-slot='radio-button-wrapper'] {
    border: none;
    padding: 8px;
    margin: 0 2px;
    height: auto;
    line-height: 20px;
    border-radius: 4px;
    &::before {
      display: none;
    }
    &.ant-radio-button-wrapper-checked,
    &[data-state='checked'] {
      &,
      &:hover {
        background-color: ${blue100};
        color: ${textPrimary};
      }
    }
  }
`
